<template>
  <LoginSingleLayout wide>
    <Alert :i18nData="asyncError" />
    <Spinner :status="loading" />
    <div class="change-pass" v-if="!loading">
      <h1 class="title-xs bold change-pass__title">
        {{ hasTempPassword ? $t('pages.changePassword.tempPassTitle') : $t('pages.changePassword.title') }}
      </h1>
      <p v-if="hasTempPassword" v-html="$t('pages.changePassword.tempPassText')" class="change-pass__intro" />

      <b-form class="change-pass__form" @submit.prevent="onSubmit">
        <div class="nl-field">
          <label class="nl-field__label" for="old-password">
            {{ $t('pages.changePassword.oldPasswordLabel') }}
          </label>
          <div class="nl-input nl-input--password">
            <input
              id="old-password"
              v-model="form.oldPassword"
              :type="showOldPassword ? 'text' : 'password'"
              autocomplete="current-password"
              class="nl-input__control"
              required
            >
            <button
              type="button"
              class="nl-eye"
              :aria-label="$t('pages.changePassword.oldPasswordLabel')"
              @click="showOldPassword = !showOldPassword"
            >
              <font-awesome-icon :icon="showOldPassword ? 'eye-slash' : 'eye'" />
            </button>
          </div>
        </div>

        <div class="nl-field">
          <label class="nl-field__label" for="new-password">
            {{ $t('pages.changePassword.newPasswordLabel') }}
          </label>
          <div class="nl-input nl-input--password">
            <input
              id="new-password"
              v-model="form.newPassword"
              :type="showNewPassword ? 'text' : 'password'"
              autocomplete="new-password"
              class="nl-input__control"
              required
            >
            <button
              type="button"
              class="nl-eye"
              :aria-label="$t('pages.changePassword.newPasswordLabel')"
              @click="showNewPassword = !showNewPassword"
            >
              <font-awesome-icon :icon="showNewPassword ? 'eye-slash' : 'eye'" />
            </button>
          </div>
          <p class="body-xs change-pass__hint" v-html="$t('pages.changePassword.passwordDescription')" />
        </div>

        <div class="change-pass__actions">
          <button
            type="submit"
            class="nl-submit nl-submit--brand"
            :disabled="form.newPassword.length < 8 || loading"
          >
            {{ $t('pages.changePassword.buttonOk') }}
          </button>
        </div>
      </b-form>
    </div>
  </LoginSingleLayout>
</template>

<script>
import LoginSingleLayout from '@/components/LoginSingleLayout.vue'
import PasswordService from '@/services/PasswordService'
import { getRedirectUri, getValueByName } from '@/utils/url'
import { turnOffZoom } from '@/utils'
import { mapState } from 'vuex'
import store from '@/store'
import { UPDATE_HAS_TEMP_PASSWORD } from '@/store/mutationTypes'

export default {
  name: 'ChangePassword',
  components: {
    LoginSingleLayout
  },
  data () {
    return {
      form: {
        oldPassword: '',
        newPassword: ''
      },
      loading: false,
      asyncError: null,
      showOldPassword: false,
      showNewPassword: false
    }
  },
  computed: {
    ...mapState([
      'hasTempPassword'
    ])
  },
  watch: {
    form: { // clean async error on new input
      handler () { this.asyncError = null },
      deep: true
    }
  },
  methods: {
    onSubmit () {
      this.loading = true
      this.asyncError = null
      return PasswordService.changePassword(this.form).then(() => {
        this.$toasted.success(this.$t('pages.changePassword.success'))
        setTimeout(() => {
          window.location.href = getRedirectUri()
        }, 1500)
      }).catch(err => {
        this.loading = false
        if (err.code === 403) {
          this.asyncError = { key: 'pages.changePassword.oldPasswordError' }
        } else if (err.code === 400 && err.message.match(/too short/)) {
          this.asyncError = { key: 'pages.changePassword.passwordShortError' }
        } else if (err.code === 400 && err.message.match(/too long/)) {
          this.asyncError = { key: 'pages.changePassword.passwordLongError' }
        } else {
          throw err
        }
      })
    }
  },
  mounted () {
    turnOffZoom()
  },
  created () {
    const tempPassword = getValueByName('temp_password')
    if (tempPassword && tempPassword === '1') {
      store.commit(UPDATE_HAS_TEMP_PASSWORD, true)
    }
  }
}
</script>

<style lang="scss" scoped>
.change-pass {
  width: 100%;

  &__title {
    margin-bottom: $spacer*1.5;
    text-align: center;
  }

  &__intro {
    margin-bottom: $spacer*1.5;
    text-align: center;
  }

  &__form {
    width: 100%;
  }

  &__hint {
    margin: $spacer*0.75 0 0;
    color: $nl-text-muted;
  }

  &__actions {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: $spacer*2.5;
  }
}
</style>
